package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.*
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun AdminScreen(
    clubId: String,
    viewModel: AdminViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    onBackClick: () -> Unit,
    navigateTo: (AdminViewModel.AdminNavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = { viewModel.fetchAdminRole() },
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> AdminScreenContent(
                    uiState = state.data,
                    onBackClick = onBackClick,
                    onManageEventsClick = { viewModel.navigateToManageEvents() },
                    onManageClubClick = { viewModel.navigateToManageClub() },
                    onManageMembersClick = { viewModel.navigateToManageMembers(state.data.role) },
                    onTakeAttendanceClick = { viewModel.navigateToTakeAttendance() },
                )
            }
        }
    }
}

@Composable
private fun AdminScreenContent(
    uiState: AdminUiState,
    onBackClick: () -> Unit,
    onManageEventsClick: () -> Unit,
    onManageClubClick: () -> Unit,
    onManageMembersClick: () -> Unit,
    onTakeAttendanceClick: () -> Unit,
) {
    val role = uiState.role
    val isSuperAdmin = role == UserRole.SUPER_ADMIN
    val isAdmin = role == UserRole.ADMIN || isSuperAdmin
    val actions = adminActions(
        isSuperAdmin = isSuperAdmin,
        onManageEventsClick = onManageEventsClick,
        onManageClubClick = onManageClubClick,
        onManageMembersClick = onManageMembersClick,
        onTakeAttendanceClick = onTakeAttendanceClick,
    )

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        AdminHeader(onBackClick = onBackClick)

        Spacer(modifier = Modifier.height(52.dp))

        Row(
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_dashboard_grid),
                contentDescription = null,
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(modifier = Modifier.width(16.dp))
            Text(
                text = stringResource(Res.string.admin_actions),
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge,
            )
        }

        Spacer(modifier = Modifier.height(28.dp))

        if (isAdmin) {
            Column(
                verticalArrangement = Arrangement.spacedBy(24.dp),
            ) {
                actions.forEach { action ->
                    AdminActionCard(action = action)
                }
            }
        } else {
            NoAdminAccessCard()
        }
    }
}

@Composable
private fun AdminHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {

        IconButton(
            onClick = onBackClick,
            modifier = Modifier.align(Alignment.Top).size(24.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(16.dp))

        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = stringResource(Res.string.admin_dashboard_title),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(modifier = Modifier.width(10.dp))
                Icon(
                    painter = painterResource(Res.drawable.ic_shield_star),
                    contentDescription = stringResource(Res.string.shield_icon_cd),
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.primary,
                )
            }

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(Res.string.admin_page_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun AdminActionCard(action: AdminAction) {
    Surface(
        onClick = action.onClick,
        modifier = Modifier
            .fillMaxWidth()
            .height(128.dp),
        shape = RoundedCornerShape(28.dp),
        color = action.accent.copy(alpha = 0.06f),
        border = BorderStroke(1.dp, action.accent.copy(alpha = 0.18f)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(72.dp)
                    .shadow(
                        elevation = 18.dp,
                        shape = RoundedCornerShape(24.dp),
                        ambientColor = action.accent.copy(alpha = 0.18f),
                        spotColor = action.accent.copy(alpha = 0.18f),
                    )
                    .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(24.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .size(50.dp)
                        .background(
                            brush = Brush.linearGradient(
                                listOf(action.accent.copy(alpha = 0.82f), action.accent)
                            ),
                            shape = RoundedCornerShape(14.dp),
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        painter = painterResource(action.icon),
                        contentDescription = null,
                        modifier = Modifier.size(34.dp),
                        tint = MaterialTheme.colorScheme.onPrimary,
                    )
                }
            }

            Spacer(modifier = Modifier.width(28.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(action.title),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(10.dp))

                Text(
                    text = stringResource(action.description),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            Box(
                modifier = Modifier
                    .size(24.dp)
                    .background(action.accent.copy(alpha = 0.12f), RoundedCornerShape(26.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_chevron_right),
                    contentDescription = stringResource(Res.string.chevron_icon_cd),
                    modifier = Modifier.size(24.dp),
                    tint = action.accent,
                )
            }
        }
    }
}

@Composable
private fun NoAdminAccessCard() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_shield_star),
                contentDescription = null,
                modifier = Modifier.size(48.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = stringResource(Res.string.admin_no_access_title),
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge,
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = stringResource(Res.string.admin_no_access_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

@Composable
private fun adminActions(
    isSuperAdmin: Boolean,
    onManageEventsClick: () -> Unit,
    onManageClubClick: () -> Unit,
    onManageMembersClick: () -> Unit,
    onTakeAttendanceClick: () -> Unit,
): List<AdminAction> {
    val colorScheme = MaterialTheme.colorScheme
    val superAdminActions = listOf(
        AdminAction(
            title = Res.string.manage_events,
            description = Res.string.manage_events_description,
            icon = Res.drawable.ic_calendar,
            accent = colorScheme.accentPurple,
            onClick = onManageEventsClick,
        ),
        AdminAction(
            title = Res.string.manage_club,
            description = Res.string.manage_club_description,
            icon = Res.drawable.ic_document_edit,
            accent = colorScheme.accentGreen,
            onClick = onManageClubClick,
        ),
    )
    val adminActions = listOf(
        AdminAction(
            title = Res.string.manage_members,
            description = Res.string.manage_members_description,
            icon = Res.drawable.ic_clubs,
            accent = colorScheme.accentOrange,
            onClick = onManageMembersClick,
        ),
        AdminAction(
            title = Res.string.take_attendance,
            description = Res.string.take_attendance_description,
            icon = Res.drawable.ic_clipboard_check,
            accent = colorScheme.accentBlue,
            onClick = onTakeAttendanceClick,
        ),
    )

    return if (isSuperAdmin) superAdminActions + adminActions else adminActions
}

private data class AdminAction(
    val title: org.jetbrains.compose.resources.StringResource,
    val description: org.jetbrains.compose.resources.StringResource,
    val icon: DrawableResource,
    val accent: Color,
    val onClick: () -> Unit = {},
)

@Preview(name = "Admin Content - Super Admin")
@Composable
private fun AdminScreenContentSuperAdminPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                AdminScreenContent(
                    uiState = AdminUiState(UserRole.SUPER_ADMIN, ongoingEventId = "event-id"),
                    onBackClick = {},
                    onManageEventsClick = {},
                    onManageClubClick = {},
                    onManageMembersClick = {},
                    onTakeAttendanceClick = {},
                )
            }
        }
    }
}

@Preview(name = "Admin Content - Admin")
@Composable
private fun AdminScreenContentAdminPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                AdminScreenContent(
                    uiState = AdminUiState(UserRole.ADMIN, ongoingEventId = "event-id"),
                    onBackClick = {},
                    onManageEventsClick = {},
                    onManageClubClick = {},
                    onManageMembersClick = {},
                    onTakeAttendanceClick = {},
                )
            }
        }
    }
}

@Preview(name = "Admin Content - No Access")
@Composable
private fun AdminScreenContentNoAccessPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                AdminScreenContent(
                    uiState = AdminUiState(UserRole.MEMBER, ongoingEventId = null),
                    onBackClick = {},
                    onManageEventsClick = {},
                    onManageClubClick = {},
                    onManageMembersClick = {},
                    onTakeAttendanceClick = {},
                )
            }
        }
    }
}

@Preview(name = "Admin Header")
@Composable
private fun AdminHeaderPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                AdminHeader(onBackClick = {})
            }
        }
    }
}

@Preview(name = "Admin Action Card")
@Composable
private fun AdminActionCardPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                AdminActionCard(
                    action = adminActions(
                        isSuperAdmin = true,
                        onManageEventsClick = {},
                        onManageClubClick = {},
                        onManageMembersClick = {},
                        onTakeAttendanceClick = {},
                    ).first()
                )
            }
        }
    }
}

@Preview(name = "No Admin Access Card")
@Composable
private fun NoAdminAccessCardPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                NoAdminAccessCard()
            }
        }
    }
}
